import { createStore } from 'react-sweet-state';
import { cloneDeep, orderBy } from 'lodash';

import {
  createSuggestionFreestyleTool,
  deleteSuggestionFreestyleTool,
  querySuggestionFreestyleTools,
  updateSuggestionFreestyleTool,
} from 'src/graphql/freestyleTools';
import { captureMessage } from 'src/utils/lib/sentry';

const STORE_NAME = 'freestyle-tools-store';

const INITIAL_STATE = {
  freestyleTools: [],
  loadingFreestyleTools: false,
  savingFreestyleTool: false,
  deletingFreestyleTool: false,
};

const sortTools = (tools) => orderBy(tools, ['name', 'id'], ['asc', 'asc']);

/**
 * Private actions
 */
const PRIVATE_ACTIONS = {
  clearStore:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  mergeToolInCache:
    (tool) =>
    ({ getState, setState }) => {
      const { freestyleTools: list } = getState();
      const newList = list.filter((w) => w.id !== tool.id);
      const sortedList = sortTools([...newList, tool]);
      setState({ freestyleTools: sortedList });
    },

  removeToolFromCache:
    (toolId) =>
    ({ getState, setState }) => {
      const { freestyleTools } = getState();
      const filteredTools = freestyleTools.filter((t) => t.id !== toolId);
      setState({ freestyleTools: [...filteredTools] });
    },
};

/**
 * Public actions
 */
const ACTIONS = {
  init:
    () =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.clearStore());
    },

  queryFreestyleTools:
    () =>
    async ({ setState }, { currentCustomerId }) => {
      setState({ loadingFreestyleTools: true });
      try {
        const result = await querySuggestionFreestyleTools(currentCustomerId);

        const sortedTools = sortTools(result);
        setState({ freestyleTools: sortedTools });
      } catch (error) {
        captureMessage('Error while getting freestyle tools', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'FreestyleToolsStore -> queryFreestyleTools',
          },
        });
      } finally {
        setState({ loadingFreestyleTools: false });
      }
    },

  getFreestyleTool:
    (toolId) =>
    ({ getState }) => {
      const { freestyleTools } = getState();
      return freestyleTools.find((t) => t.id === toolId);
    },

  createFreestyleTool:
    (tool) =>
    async ({ setState, dispatch }, { currentCustomerId }) => {
      setState({ savingFreestyleTool: true });
      try {
        const createdTool = await createSuggestionFreestyleTool({
          customerId: currentCustomerId,
          name: tool.name,
          allInstructions: tool.allInstructions,
        });
        dispatch(PRIVATE_ACTIONS.mergeToolInCache(createdTool));
        return createdTool;
      } finally {
        setState({ savingFreestyleTool: false });
      }
    },

  updateFreestyleTool:
    (tool) =>
    async ({ setState, dispatch }) => {
      setState({ savingFreestyleTool: true });
      try {
        const updatedTool = await updateSuggestionFreestyleTool({
          id: tool.id,
          name: tool.name,
          allInstructions: tool.allInstructions,
        });
        dispatch(PRIVATE_ACTIONS.mergeToolInCache(updatedTool));
        return updatedTool;
      } finally {
        setState({ savingFreestyleTool: false });
      }
    },

  deleteFreestyleTool:
    (toolId) =>
    async ({ setState, dispatch }) => {
      setState({ deletingFreestyleTool: true });
      try {
        await deleteSuggestionFreestyleTool(toolId);
        dispatch(PRIVATE_ACTIONS.removeToolFromCache(toolId));
      } finally {
        setState({ deletingFreestyleTool: false });
      }
    },
};

export const FreestyleToolsStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: STORE_NAME,
});
